import React, { useState } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { X, Check, Columns3, GripVertical } from 'lucide-react';
import {
  activityToolbarButtonClass,
  popupRowItemClass,
} from 'features/activity/styles/dataTableStyles';
import { primary } from 'shared/theme/trodoColors';

/**
 * Edit-columns popup for the Runs and Spans tables.
 *
 * Both tables had a flat checkbox list — you could choose columns but not order
 * them, while every Activity table has offered drag-to-reorder for ages. The
 * order was always meaningful (`resolveRunColumns` / `resolveSpanColumns` render
 * the enabled keys in array order); it just wasn't reachable from the UI.
 *
 * Same two-section shape as the Activity modals — Active (draggable) above,
 * Available (toggles) below — and the same react-dnd backend, so the
 * interaction is identical wherever a user meets it.
 *
 * `columns`: `[{ key, label }]` — every configurable column, in catalogue order.
 * `columnKeys`: the enabled keys, in display order. `onChange` receives the same.
 */
export default function ColumnsPopover({ columns, columnKeys, defaultKeys, onChange, title = 'Edit Columns' }) {
  const [open, setOpen] = useState(false);

  const byKey = new Map(columns.map((c) => [c.key, c]));
  const active = columnKeys.filter((k) => byKey.has(k));
  const activeSet = new Set(active);

  const toggle = (key) => {
    onChange(activeSet.has(key) ? columnKeys.filter((k) => k !== key) : [...columnKeys, key]);
  };

  const move = (from, to) => {
    if (from === to) return;
    const next = [...active];
    const [removed] = next.splice(from, 1);
    next.splice(to, 0, removed);
    onChange(next);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={activityToolbarButtonClass}
        title="Edit columns"
      >
        <Columns3 size={14} className="text-[color:var(--text-muted)]" />
        Edit columns
      </button>

      {open && (
        <DndProvider backend={HTML5Backend}>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0"
              style={{ background: 'var(--surface-overlay)' }}
              onClick={() => setOpen(false)}
            />

            <div
              className="relative flex flex-col overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-surface shadow-token-lg"
              style={{ width: '100%', maxWidth: 360, maxHeight: 'min(90vh, 480px)' }}
            >
              <div className="flex h-10 shrink-0 items-center justify-between border-b border-[color:var(--border-default)] px-4">
                <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">{title}</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onChange([...defaultKeys])}
                    className="text-[11px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    className="flex h-6 w-6 items-center justify-center rounded text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>

              <div className="scrollbar-subtle flex-1 overflow-y-auto">
                {active.length > 0 ? (
                  <div className="px-3 pb-1 pt-3">
                    <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                      Active · drag to reorder
                    </p>
                    {active.map((key, index) => (
                      <DraggableColumn
                        key={key}
                        index={index}
                        label={byKey.get(key)?.label || key}
                        onMove={move}
                        onRemove={() => toggle(key)}
                      />
                    ))}
                  </div>
                ) : null}

                {active.length > 0 ? <div className="mx-3 mt-2 border-t border-[color:var(--border-default)]" /> : null}

                <div className="px-3 pb-3 pt-2.5">
                  <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
                    Available
                  </p>
                  {columns.map((c) => {
                    const isOn = activeSet.has(c.key);
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => toggle(c.key)}
                        className={popupRowItemClass}
                      >
                        <span
                          className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded border transition-colors"
                          style={{
                            borderColor: isOn ? primary : 'var(--border-strong)',
                            backgroundColor: isOn ? primary : 'transparent',
                          }}
                        >
                          {isOn && <Check size={9} strokeWidth={3} className="text-ink-onBrand" />}
                        </span>
                        <span className="flex-1 truncate">{c.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </DndProvider>
      )}
    </>
  );
}

/** One row of the Active list. Hover swaps it with whatever it's dragged over. */
function DraggableColumn({ index, label, onMove, onRemove }) {
  const [{ isDragging }, drag] = useDrag({
    type: 'agent-column',
    item: () => ({ index }),
    collect: (m) => ({ isDragging: m.isDragging() }),
  });
  const [{ isOver }, drop] = useDrop({
    accept: 'agent-column',
    hover: (item) => {
      if (item.index !== index) {
        onMove(item.index, index);
        // Keep the dragged item's index in sync so one drag can walk past
        // several rows instead of thrashing between two.
        item.index = index;
      }
    },
    collect: (m) => ({ isOver: m.isOver() }),
  });

  return (
    <div
      ref={(n) => drag(drop(n))}
      className={`group/col flex cursor-move items-center gap-2 rounded px-2 py-1.5 transition-colors ${
        isDragging ? 'opacity-40' : isOver ? 'bg-[color:var(--border-subtle)]' : 'hover:bg-[color:var(--surface-sunken)]'
      }`}
    >
      <GripVertical size={13} className="shrink-0 text-[color:var(--border-strong)]" />
      <span className="flex-1 truncate text-[12px] text-[color:var(--text-primary)]">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        title="Remove column"
        className="flex h-4 w-4 flex-none items-center justify-center rounded text-[color:var(--text-muted)] opacity-0 transition-opacity hover:bg-surface-hover hover:text-[color:var(--text-primary)] group-hover/col:opacity-100"
      >
        <X size={11} />
      </button>
    </div>
  );
}
